/**
 * The cadence section's three rows — KANON `04-edytor.html`: how often, which day, and from when.
 *
 * Which row is drawn follows the frequency, because the other two fields mean nothing under it: a
 * daily rhythm has no day to choose. The chosen weekday takes the flame GRADIENT, never a flat
 * orange, and the chosen SEGMENT does not — the canon's `.seg span.on` is a raised card-coloured
 * pill and the gradient belongs to `.wd.on` alone. A segment is a setting; the accent marks an act.
 *
 * The three controls are `canon.module.css`'s. The segment used to be re-drawn here with `0 48px`
 * of option padding against the export's `0 14px`, which made one row about three times as wide as
 * the drawing and was the first thing the owner pointed at on 17 September 2026.
 *
 * ── „Start od" is a FIELD with the glyph inside it ───────────────────────────────────────────
 * The canon draws a 36px inset frame with a calendar glyph at its head and the date beside it. The
 * native control's own picker indicator is stretched over that frame at zero opacity rather than
 * suppressed — see the stylesheet. Suppressing it outright leaves a field that can only be typed
 * into, which on a date is a real loss for anyone using a pointer.
 *
 * The values it holds are never cleared when the frequency changes: `toCadenceInput` sends only the
 * field the chosen frequency uses, so a weekday remembered from a visit to the weekly segment costs
 * nothing and saves the trainer re-picking it.
 */

import { useId, useState, type FocusEvent } from 'react';

import { CalendarIcon } from '~/shared/ui/AuthIcons';

import {
  MAX_DAY_OF_MONTH,
  MIN_DAY_OF_MONTH,
  WEEKDAY_CHOICES,
  cadenceProblemMessage,
  cadenceProblems,
  type CadenceDraft,
  type CadenceProblem,
} from '../model/cadenceForm';
import type { SurveyCadence } from '../model/cadenceForm';
import type { TranslateKey } from '../model/translateKey';
import seg from '~/shared/ui/segmented.module.css';
import canon from '~/shared/ui/surveyCanon.module.css';
import styles from './EditorForm.module.css';

const FREQUENCIES: readonly SurveyCadence[] = ['Day', 'Week', 'Month'];

export interface CadenceFieldsProps {
  readonly cadence: CadenceDraft;
  readonly onChange: (patch: Partial<CadenceDraft>) => void;
  readonly t: TranslateKey;
}

/**
 * What is still missing, said under the field it belongs to and only once the trainer has LEFT that
 * field. A pristine form opened on two red sentences under the card's edge (visual audit,
 * 2 November 2026): nothing had gone wrong yet, and a refused save already says why on the save
 * control itself.
 */
function useLeftFields() {
  const [left, setLeft] = useState<ReadonlySet<CadenceProblem>>(() => new Set());
  const leave = (field: CadenceProblem) => (event: FocusEvent<HTMLElement>) => {
    // A group is left when focus moves outside it, not from one weekday to the next.
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    setLeft((current) => (current.has(field) ? current : new Set(current).add(field)));
  };
  return { left, leave };
}

export function CadenceFields({ cadence, onChange, t }: CadenceFieldsProps) {
  const startId = useId();
  const monthId = useId();
  const weekdayLabelId = useId();
  const problemId = useId();
  const { left, leave } = useLeftFields();
  const missing = cadenceProblems(cadence);
  const shown = (field: CadenceProblem): boolean => missing.includes(field) && left.has(field);
  const problemNote = (field: CadenceProblem) =>
    shown(field) ? (
      <p id={`${problemId}-${field}`} className={styles.problem}>
        {cadenceProblemMessage(field, t)}
      </p>
    ) : null;

  return (
    <div className={styles.cadence}>
      <div className={styles.cadenceRow}>
        <span className={styles.cadenceLabel} id={`${weekdayLabelId}-freq`}>
          {t('web:reports.editor.cadence.howOften')}
        </span>
        <div className={seg.seg} role="group" aria-labelledby={`${weekdayLabelId}-freq`}>
          {FREQUENCIES.map((frequency) => (
            <button
              key={frequency}
              type="button"
              className={`${String(seg.segOption)} ${
                cadence.frequency === frequency ? String(seg.segOptionOn) : ''
              }`}
              aria-pressed={cadence.frequency === frequency}
              onClick={() => {
                onChange({ frequency });
              }}
            >
              {t(`web:reports.editor.cadence.frequency.${frequency}`)}
            </button>
          ))}
        </div>
      </div>

      {cadence.frequency === 'Week' ? (
        <div className={styles.cadenceRow}>
          <span className={styles.cadenceLabel} id={weekdayLabelId}>
            {t('web:reports.editor.cadence.whichDay')}
          </span>
          <div className={styles.cadenceControl}>
            <div
              className={styles.weekdays}
              role="group"
              aria-labelledby={weekdayLabelId}
              aria-describedby={shown('dayOfWeek') ? `${problemId}-dayOfWeek` : undefined}
              onBlur={leave('dayOfWeek')}
            >
              {WEEKDAY_CHOICES.map((choice) => (
                <button
                  key={choice.isoKey}
                  type="button"
                  className={`${String(canon.wd)} ${
                    cadence.dayOfWeek === choice.dayOfWeek ? String(canon.wdOn) : ''
                  }`}
                  aria-pressed={cadence.dayOfWeek === choice.dayOfWeek}
                  aria-label={t(`web:weekdays.full.${choice.isoKey}`)}
                  onClick={() => {
                    onChange({ dayOfWeek: choice.dayOfWeek });
                  }}
                >
                  {t(`web:weekdays.short.${choice.isoKey}`)}
                </button>
              ))}
          </div>
          {problemNote('dayOfWeek')}
          </div>
        </div>
      ) : null}

      {cadence.frequency === 'Month' ? (
        <div className={styles.cadenceRow}>
          <label className={styles.cadenceLabel} htmlFor={monthId}>
            {t('web:reports.editor.cadence.whichDayOfMonth')}
          </label>
          <div className={styles.cadenceControl}>
            <span className={`${String(canon.field)} ${String(styles.monthFrame)}`}>
              <CalendarIcon className={canon.fieldIcon} />
              <input
                id={monthId}
                aria-invalid={shown('dayOfMonth') || undefined}
                aria-describedby={shown('dayOfMonth') ? `${problemId}-dayOfMonth` : undefined}
                onBlur={leave('dayOfMonth')}
                className={styles.monthField}
                type="number"
                inputMode="numeric"
                min={MIN_DAY_OF_MONTH}
                max={MAX_DAY_OF_MONTH}
                value={cadence.dayOfMonth ?? ''}
                onChange={(event) => {
                  const parsed = Number.parseInt(event.target.value, 10);
                  onChange({
                    dayOfMonth:
                      Number.isInteger(parsed) && parsed >= MIN_DAY_OF_MONTH && parsed <= MAX_DAY_OF_MONTH
                        ? parsed
                        : null,
                  });
                }}
              />
            </span>
            {problemNote('dayOfMonth')}
          </div>
        </div>
      ) : null}

      <div className={styles.cadenceRow}>
        <label className={styles.cadenceLabel} htmlFor={startId}>
          {t('web:reports.editor.cadence.startFrom')}
        </label>
        <div className={styles.cadenceControl}>
          <span className={`${String(canon.field)} ${String(styles.dateFrame)}`}>
            <CalendarIcon className={canon.fieldIcon} />
            <input
              id={startId}
              aria-invalid={shown('startDate') || undefined}
              aria-describedby={shown('startDate') ? `${problemId}-startDate` : undefined}
              onBlur={leave('startDate')}
              className={styles.dateField}
              type="date"
              value={cadence.startDate ?? ''}
              onChange={(event) => {
                onChange({ startDate: event.target.value === '' ? null : event.target.value });
              }}
            />
          </span>
          {problemNote('startDate')}
        </div>
      </div>
    </div>
  );
}
